/**
 * Responsive utilities for mobile-first portrait gameplay.
 */

import { GAME_HEIGHT, GAME_WIDTH, SAFE_AREA, UI_SIZES } from '../config';

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface LayoutBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

function parsePx(value: string | null | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getViewportMetrics(): { width: number; height: number; offsetLeft: number; offsetTop: number } {
  const viewport = window.visualViewport;
  if (!viewport) {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      offsetLeft: 0,
      offsetTop: 0,
    };
  }

  return {
    width: viewport.width,
    height: viewport.height,
    offsetLeft: viewport.offsetLeft,
    offsetTop: viewport.offsetTop,
  };
}

export function setupMobileViewportHarness(): void {
  const root = document.documentElement;

  const applyViewport = (): void => {
    const viewport = getViewportMetrics();
    root.style.setProperty('--app-height', `${viewport.height}px`);
    root.style.setProperty('--app-width', `${viewport.width}px`);
  };

  applyViewport();
  setupSafeAreaProperties();

  window.addEventListener('resize', applyViewport);
  window.addEventListener('orientationchange', applyViewport);

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', applyViewport);
    window.visualViewport.addEventListener('scroll', applyViewport);
  }
}

/**
 * Populates CSS custom properties with browser-resolved safe area values.
 */
export function setupSafeAreaProperties(): void {
  const root = document.documentElement;

  const probe = document.createElement('div');
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.top = '0';
  probe.style.left = '0';
  probe.style.paddingTop = 'env(safe-area-inset-top)';
  probe.style.paddingBottom = 'env(safe-area-inset-bottom)';
  probe.style.paddingLeft = 'env(safe-area-inset-left)';
  probe.style.paddingRight = 'env(safe-area-inset-right)';
  document.body.appendChild(probe);

  const computed = getComputedStyle(probe);
  const top = parsePx(computed.paddingTop, SAFE_AREA.top);
  const bottom = parsePx(computed.paddingBottom, SAFE_AREA.bottom);
  const left = parsePx(computed.paddingLeft, 0);
  const right = parsePx(computed.paddingRight, 0);

  probe.remove();

  root.style.setProperty('--safe-area-inset-top', `${top}px`);
  root.style.setProperty('--safe-area-inset-bottom', `${bottom}px`);
  root.style.setProperty('--safe-area-inset-left', `${left}px`);
  root.style.setProperty('--safe-area-inset-right', `${right}px`);
}

export function getSafeAreaInsets(): SafeAreaInsets {
  const computed = getComputedStyle(document.documentElement);

  return {
    top: parsePx(computed.getPropertyValue('--safe-area-inset-top'), SAFE_AREA.top),
    bottom: parsePx(computed.getPropertyValue('--safe-area-inset-bottom'), SAFE_AREA.bottom),
    left: parsePx(computed.getPropertyValue('--safe-area-inset-left'), 0),
    right: parsePx(computed.getPropertyValue('--safe-area-inset-right'), 0),
  };
}

export function getScaleFactor(): number {
  const viewport = getViewportMetrics();
  const scaleX = viewport.width / GAME_WIDTH;
  const scaleY = viewport.height / GAME_HEIGHT;
  return Math.min(scaleX, scaleY);
}

export function gameToScreen(gameX: number, gameY: number): { x: number; y: number } {
  const viewport = getViewportMetrics();
  const scale = getScaleFactor();

  const offsetX = (viewport.width - GAME_WIDTH * scale) / 2 + viewport.offsetLeft;
  const offsetY = (viewport.height - GAME_HEIGHT * scale) / 2 + viewport.offsetTop;

  return {
    x: gameX * scale + offsetX,
    y: gameY * scale + offsetY,
  };
}

export function screenToGameStable(screenX: number, screenY: number): { x: number; y: number } {
  const viewport = getViewportMetrics();
  const scale = getScaleFactor();

  const offsetX = (viewport.width - GAME_WIDTH * scale) / 2 + viewport.offsetLeft;
  const offsetY = (viewport.height - GAME_HEIGHT * scale) / 2 + viewport.offsetTop;

  return {
    x: (screenX - offsetX) / scale,
    y: (screenY - offsetY) / scale,
  };
}

export function screenToGame(screenX: number, screenY: number): { x: number; y: number } {
  return screenToGameStable(screenX, screenY);
}

export function getPlayableBounds(trackPadding: number = 20): LayoutBounds {
  const safe = getSafeAreaInsets();
  const scale = getScaleFactor();

  const safeTopGame = safe.top / scale;
  const safeBottomGame = safe.bottom / scale;
  const safeLeftGame = safe.left / scale;
  const safeRightGame = safe.right / scale;

  const left = trackPadding + safeLeftGame;
  const right = GAME_WIDTH - trackPadding - safeRightGame;
  const top = safeTopGame;
  const bottom = GAME_HEIGHT - safeBottomGame;

  return {
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

export function getHudBounds(hudHeight: number = 56): LayoutBounds {
  const playable = getPlayableBounds(0);
  const top = playable.top;
  const bottom = top + hudHeight;

  return {
    left: playable.left,
    right: playable.right,
    top,
    bottom,
    width: playable.width,
    height: hudHeight,
  };
}

export function isValidTouchTarget(width: number, height: number): boolean {
  return width >= UI_SIZES.minTouchTarget && height >= UI_SIZES.minTouchTarget;
}

export function getMinTouchTargetSize(): number {
  const scale = getScaleFactor();
  return Math.ceil(UI_SIZES.minTouchTarget / scale);
}

export function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth;
}

export function getDeviceInfo(): {
  screenWidth: number;
  screenHeight: number;
  gameScale: number;
  pixelRatio: number;
  isPortrait: boolean;
  isTouchDevice: boolean;
} {
  return {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    gameScale: getScaleFactor(),
    pixelRatio: window.devicePixelRatio || 1,
    isPortrait: isPortrait(),
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
  };
}
